<div class="flex flex-col gap-6">
  <div>
    <h2 class="text-2xl font-bold">Request Custom Plan</h2>
    <p class="mt-2 text-base text-gray-600">
      Fill out the form below and we'll get back to you with a tailored pricing
      plan.
    </p>
  </div>

  <form [formGroup]="formGroup" (ngSubmit)="handleSubmit()">
    <div class="tui-form__row flex flex-col gap-4 md:flex-row">
      <div class="flex-1">
        <tui-textfield>
          <label tuiLabel>First name <span class="tui-required"></span></label>
          <input [formControl]="formGroup.controls.firstName" tuiTextfield />
        </tui-textfield>
        <tui-error
          formControlName="firstName"
          [error]="[] | tuiFieldError | async"
        />
      </div>
      <div class="flex-1">
        <tui-textfield>
          <label tuiLabel>Last name <span class="tui-required"></span></label>
          <input [formControl]="formGroup.controls.lastName" tuiTextfield />
        </tui-textfield>
        <tui-error
          formControlName="lastName"
          [error]="[] | tuiFieldError | async"
        />
      </div>
    </div>

    <div class="tui-form__row">
      <tui-textfield>
        <label tuiLabel>Company</label>
        <input [formControl]="formGroup.controls.company" tuiTextfield />
      </tui-textfield>
      <tui-error
        formControlName="company"
        [error]="[] | tuiFieldError | async"
      />
    </div>

    <div class="tui-form__row">
      <tui-textfield>
        <label tuiLabel>Email <span class="tui-required"></span></label>
        <input [formControl]="formGroup.controls.email" tuiTextfield />
      </tui-textfield>
      <tui-error formControlName="email" [error]="[] | tuiFieldError | async" />
    </div>

    <div class="tui-form__row">
      <tui-textfield multi tuiChevron [stringify]="stringifyFeature">
        <label tuiLabel
          >Which features are you interested in?
          <span class="tui-required"></span
        ></label>
        <input tuiInputChip [formControl]="formGroup.controls.features" />
        <tui-input-chip *tuiItem />
        <tui-data-list *tuiTextfieldDropdown tuiMultiSelectGroup>
          @for (feature of features; track feature) {
            <button new tuiOption type="button" [value]="feature">
              {{ feature.label }}
            </button>
          }
        </tui-data-list>
      </tui-textfield>
      <tui-error
        formControlName="features"
        [error]="[] | tuiFieldError | async"
      />
    </div>

    @if (showLiveUpdatesFields) {
      <div class="tui-form__row">
        <tui-textfield>
          <label tuiLabel
            >Estimated number of Monthly Active Users (MAU) receiving Live
            Updates <span class="tui-required"></span
          ></label>
          <input
            [formControl]="formGroup.controls.monthlyActiveDevices"
            tuiInputNumber
          />
          <tui-icon [tuiTooltip]="monthlyActiveDevicesHint" />
          <ng-template #monthlyActiveDevicesHint>
            This is the total number of unique devices you expect to receive
            Live Updates each month (e.g., 10,000 MAU).
          </ng-template>
        </tui-textfield>
        <tui-error
          formControlName="monthlyActiveDevices"
          [error]="[] | tuiFieldError | async"
        />
      </div>

      <div class="tui-form__row">
        <tui-textfield>
          <label tuiLabel
            >Estimated number of Live Updates you plan to publish per month
            <span class="tui-required"></span
          ></label>
          <input
            [formControl]="formGroup.controls.updatesPerMonth"
            tuiInputNumber
          />
          <tui-icon [tuiTooltip]="updatesPerMonthHint" />
          <ng-template #updatesPerMonthHint>
            This is the total number of Live Updates you expect to publish each
            month (e.g., 5 updates per month).
          </ng-template>
        </tui-textfield>
        <tui-error
          formControlName="updatesPerMonth"
          [error]="[] | tuiFieldError | async"
        />
      </div>

      <div class="tui-form__row">
        <tui-textfield>
          <label tuiLabel
            >Estimated size of each Live Update bundle (in MB)
            <span class="tui-required"></span
          ></label>
          <input
            [formControl]="formGroup.controls.updateSizeInMb"
            tuiInputNumber
          />
          <tui-icon [tuiTooltip]="updateSizeInMbHint" />
          <ng-template #updateSizeInMbHint>
            This is the size of the web bundle directory (dist/www/...) after
            you run "npm run build" (e.g., 15 MB).
          </ng-template>
        </tui-textfield>
        <tui-error
          formControlName="updateSizeInMb"
          [error]="[] | tuiFieldError | async"
        />
      </div>
    }

    @if (showNativeBuildsFields) {
      <div class="tui-form__row">
        <tui-textfield>
          <label tuiLabel
            >Estimated number of Native Builds per month
            <span class="tui-required"></span
          ></label>
          <input
            [formControl]="formGroup.controls.buildsPerMonth"
            tuiInputNumber
          />
          <tui-icon [tuiTooltip]="buildsPerMonthHint" />
          <ng-template #buildsPerMonthHint>
            This is the total number of native builds you expect to run each
            month (e.g., 100 builds per month).
          </ng-template>
        </tui-textfield>
        <tui-error
          formControlName="buildsPerMonth"
          [error]="[] | tuiFieldError | async"
        />
      </div>
    }

    @if (showAppStorePublishingFields) {
      <div class="tui-form__row">
        <tui-textfield>
          <label tuiLabel
            >Estimated number of App Submissions per month
            <span class="tui-required"></span
          ></label>
          <input
            [formControl]="formGroup.controls.submissionsPerMonth"
            tuiInputNumber
          />
          <tui-icon [tuiTooltip]="submissionsPerMonthHint" />
          <ng-template #submissionsPerMonthHint>
            This is the total number of app submissions to app stores you expect
            to make each month (e.g., 20 submissions per month).
          </ng-template>
        </tui-textfield>
        <tui-error
          formControlName="submissionsPerMonth"
          [error]="[] | tuiFieldError | async"
        />
      </div>
    }

    <div class="tui-form__row text-left">
      <tui-textfield>
        <label tuiLabel>Additional comments</label>
        <textarea
          [formControl]="formGroup.controls.additionalComments"
          tuiTextarea
          [max]="4"
          [min]="4"
        ></textarea>
      </tui-textfield>
      <tui-error
        formControlName="additionalComments"
        [error]="[] | tuiFieldError | async"
      />
    </div>

    <div class="tui-form__row mt-4 flex">
      <button
        tuiButton
        type="submit"
        appearance="primary"
        class="flex-1"
        [loading]="submitContactForm.isPending()"
      >
        Submit
      </button>
    </div>
  </form>
</div>
